
import { useState, useEffect, useCallback, useMemo } from "react";
import { isDayAvailable } from "@/lib/availability-utils";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { getDateKeyInTimezone } from "@/lib/timezone/date-utils";
import { getTimezoneAwareDayOfWeek } from "@/lib/timezone/day-of-week-utils";

interface DayAvailabilityState {
  isDayAvailable: (date: Date) => boolean;
  loading: boolean;
  error: string | null;
}

// Cache to store availability results by day and user - using timezone-aware keys
const availabilityCache: Record<string, { result: boolean; timestamp: number }> = {};
const CACHE_DURATION = 1 * 60 * 1000; // 1 minute in milliseconds

export function useDayAvailability(
  hostUserId?: string,
  organizerTimezone: string = 'UTC'
): DayAvailabilityState {
  const [availabilityMap, setAvailabilityMap] = useState<Map<string, boolean>>(new Map());
  
  const checkDayAvailability = useCallback((selectedDate: Date): boolean => {
    // Guard against undefined or invalid dates
    if (!selectedDate || !(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
      console.warn('[useDayAvailability] Invalid date passed to checkDayAvailability:', selectedDate);
      return false;
    }

    // Use timezone-aware cache key instead of toDateString
    const dateKey = getDateKeyInTimezone(selectedDate, organizerTimezone);
    const cacheKey = `${dateKey}_${hostUserId || 'self'}_${organizerTimezone}`;
    
    // Check cache first
    const cached = availabilityCache[cacheKey];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.result;
    }

    // For debug user, return true
    if (hostUserId === 'debug') {
      availabilityCache[cacheKey] = { result: true, timestamp: Date.now() };
      return true;
    }
    
    // Check local state cache using timezone-aware key
    const localCached = availabilityMap.get(dateKey);
    if (localCached !== undefined) {
      return localCached;
    }
    
    // Trigger async check and cache result with timezone awareness
    if (hostUserId) {
      isDayAvailable(hostUserId, selectedDate, supabase, organizerTimezone)
        .then((available) => {
          availabilityCache[cacheKey] = {
            result: available,
            timestamp: Date.now()
          };
          setAvailabilityMap(prev => new Map(prev.set(dateKey, available)));
          
          // Debug logging for day-of-week mapping
          const timezoneAwareDay = getTimezoneAwareDayOfWeek(selectedDate, organizerTimezone);
          const utcDay = selectedDate.getDay();
          console.log(`[useDayAvailability] Date: ${selectedDate.toDateString()}, UTC day: ${utcDay}, ${organizerTimezone} day: ${timezoneAwareDay}, Available: ${available}`);
        })
        .catch((err) => {
          console.error("[useDayAvailability] Error:", err);
          availabilityCache[cacheKey] = {
            result: false,
            timestamp: Date.now() - (CACHE_DURATION / 2)
          };
          setAvailabilityMap(prev => new Map(prev.set(dateKey, false)));
        });
    }
    
    // Return optimistic true while loading
    return true;
  }, [hostUserId ?? '', availabilityMap.size, organizerTimezone ?? 'UTC']);

  return { 
    isDayAvailable: checkDayAvailability,
    loading: false,
    error: null
  };
}
